import { useCallback, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import classes from "../homePageNew.module.css";
import { fetchReportHistory, cancelReport } from "../api/homePageApi";
import { RetryIcon, CategoryIcon } from "./icons";
import ReportHistoryTable from "./ReportHistoryTable";

/**
 * "N more reports for this run" — same design as
 * DiscoveryV2/Reporting/DiscoveryReportingModal.js's own report history
 * (title, info banner, refresh, table with delete), minus its "Generate
 * JSON"/"Generate New Version" triggers, which don't apply here. Calls the
 * real html-history endpoint only when the modal opens (never on page
 * load), with a loader until the response resolves.
 *
 * Each row's eye icon closes this modal and opens the report in the same
 * ReportPreviewDrawer ReportStrip.js's own row uses — onViewReport is the
 * shared callback that owns that drawer's state (see DiscoveryModule.js).
 * Delete reuses the same /llm-reporting/cancel/:jobId call the real modal's
 * delete action calls (there's no separate delete endpoint) and removes the
 * row locally on success, same optimistic-update behavior as that modal.
 */
export default function ReportsModal({ open, onClose, transactionId, userId, brand, moduleKey, onViewReport }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(() => {
    if (!transactionId || !userId) return;
    setLoading(true);
    fetchReportHistory({ userId, transactionId, moduleKey })
      .then((data) => setRows(data))
      .finally(() => setLoading(false));
  }, [transactionId, userId, moduleKey]);

  useEffect(() => {
    if (!open) return;
    setRows([]);
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transactionId, userId, moduleKey]);

  if (!open) return null;

  // Category Analysis has no main brand — its reports are a competitive
  // leaderboard across several competitor brands, not one brand's own
  // report — so the Brand column is dropped there and the modal narrows to
  // match (one fewer column to fit).
  const showBrand = moduleKey !== "categoryAnalysis";

  const handleView = (row) => {
    onViewReport({ jobId: row.jobId, category: row.category });
    onClose();
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this report? This can't be undone.")) return;
    try {
      await cancelReport(row.jobId);
      setRows((prev) => prev.filter((r) => r.jobId !== row.jobId));
    } catch {
      toast.error("Couldn't delete this report. Please try again.");
    }
  };

  return (
    <>
      <div className={classes.reportsModalOverlay} onClick={onClose} />
      <div className={`${classes.reportsModal} ${!showBrand ? classes.reportsModalNarrow : ""}`}>
        <div className={classes.rpHead}>
          <div>
            <div className={classes.rpTitle}>Reporting{brand ? ` - ${brand}` : ""}</div>
            <div className={classes.rpSub}>Every report generated for this run</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button className={classes.modalClose} onClick={loadHistory} title="Refresh">
              <RetryIcon size={15} />
            </button>
            <button className={classes.modalClose} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>
        <div className={classes.reportsModalBanner}>
          <CategoryIcon size={15} />
          Use the table below to view, open, or download previous report versions.
        </div>
        <div className={classes.reportsModalBody}>
          {loading ? (
            <div className={classes.reportsModalLoading}>
              <CircularProgress size={24} />
            </div>
          ) : rows.length === 0 ? (
            <div className={classes.mdEmptyList}>No reports found for this run.</div>
          ) : (
            <ReportHistoryTable rows={rows} brand={brand} onView={handleView} onDelete={handleDelete} showBrand={showBrand} />
          )}
        </div>
      </div>
    </>
  );
}
